import { useLocation, useNavigate } from 'react-router-dom';
import { Form, FormProvider, useForm } from 'react-hook-form';
import cashIcon from '/payment(1).png?url';
import cardIcon from '/card.png?url';
import DateAndStaff from './DateAndStaff';
import {
  useFetchServiceDataById,
  useFetchUserById,
} from '../../../hook/useQueryData';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import AdditionalDetails from './AdditionalDetails';
import AddressSection from './AddressSection';
import {
  AdditionalGet,
  PackageGet,
  PersonalGet,
  ServiceGet,
} from '../../../utils/type';
import CustomChip from '../../components/CustomChip';
import { TbRosetteDiscountCheckFilled } from 'react-icons/tb';
import { LuCalendarCheck, LuClock3, LuUser } from 'react-icons/lu';
import moment from 'moment';
import { yupResolver } from '@hookform/resolvers/yup';
import { personalInfoSchema } from '../../../utils/validation/personalInfoSchema';

import { SlLocationPin } from 'react-icons/sl';
import { addToast, Divider } from '@heroui/react';
import CustomButton from '../../components/CustomButton';
import PackageSection from './PackageSection';
import { useQueryClient } from '@tanstack/react-query';
import PaymentSection, {
  PAYMENT_RESUME_KEY,
  STRIPE_PUBLISHABLE_KEY,
} from './PaymentSection';
import { FEATURE_FLAGS, useFeatureFlag } from '../../../config/featureFlags';

import {
  ANY_STAFF_ID,
  ANY_STAFF_LABEL,
  buildBookingPayload,
  checkDiscountIsValid,
  formatBookingPayload,
  generateIdempotencyKey,
  mergePaymentTax,
  validateBookingPayload,
} from '../../../utils/common';
import { apiClient } from '../../../api';
import {
  buildTaxCalculationPayload,
  getBookingById,
  getBookingByReference,
  holdBookingSlot,
  releaseHeldSlot,
} from '../../../service/bookingService';
import type { HoldSlotResponse } from '../../../service/bookingService';
import { useAuth } from '../../../context/AuthContext';
import SmallLoadingSpinner from '../../components/common/ui/SmallLoadingSpinner';
import fallbackImage from '../../../../public/fallback-image.png';
import { formatCurrency } from '../../../utils/currency';
import {
  useBookingTotalCalculate,
  useCalculateTax,
} from '../../../hook/useMutationData';
import CustomAlert from '../../components/CustomAlert';
import { ACCOUNT_STATUS } from '../../../utils/enum';
import { Seo } from '../../../core/seo';
import { useTranslation } from 'react-i18next';
import { track } from '../../../lib/telemetry';
import { toBand } from '../../../lib/telemetryUtils';

/** Booking statuses that represent a completed/confirmed booking. */
const CONFIRMED_STATUSES = new Set(['Confirmed', 'Completed', 'Finished']);

/** Payment-flow phases after the booking record is created. */
type PaymentPhase = 'idle' | 'paying' | 'confirming' | 'failed' | 'confirmed';

/** Booking-status polling cadence + deadline after payment authorization. */
const BOOKING_CONFIRM_POLL_MS = 2000;
const BOOKING_CONFIRM_TIMEOUT_MS = 90000;

/** Map RHF field names to the telemetry fieldName enum (taxonomy 01). */
const TELEMETRY_FIELD_MAP: Record<string, string> = {
  firstName: 'firstName',
  lastName: 'lastName',
  email: 'email',
  phone: 'phone',
  'address.street': 'address',
  'address.city': 'address',
  'address.state': 'address',
  'address.postalCode': 'address',
  bookingNotes: 'notes',
};

const mapValidationError = (rawType?: string): string | null => {
  if (!rawType) return null;
  if (rawType === 'required') return 'required';
  if (rawType === 'email') return 'invalid_email';
  if (rawType === 'matches' || rawType === 'typeError') return 'invalid_phone';
  return rawType;
};

const SERVICE_ENDPOINT = import.meta.env.VITE_APP_BACKEND_SERVICE;

const getInitials = (name?: string | null) => {
  if (!name) return '?';
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

/**
 * Build a human-readable tax line label from the tax quote.
 * e.g., "HST (ON) 13%" from quote with taxName='HST', jurisdiction='CA-ON', rate=0.13
 */
const getTaxLineLabel = (quote: any): string => {
  if (!quote) return 'Tax';
  if (!quote.lines || quote.lines.length === 0) return 'Tax';

  const lines = quote.lines;
  const labels = lines.map((line: any) => {
    // Extract province code from jurisdiction key (e.g., 'CA-ON' → 'ON')
    const province = line.jurisdiction?.split('-')?.[1] || '';
    const ratePercent = ((line.rate || 0) * 100).toFixed(0);
    return `${line.taxName}${province ? ` (${province})` : ''} ${ratePercent}%`;
  });

  return labels.join(' + ');
};

/**
 * Build the /booking/success navigation state from a confirmed booking.
 * `ctx`/`payload` carry live form data that isn't stored on the booking doc;
 * after a 3DS-redirect page reload they're empty, so everything falls back to
 * the booking's own fields.
 */
const buildBookingSuccessState = (
  booking: any,
  ctx: {
    service?: ServiceGet;
    timeAndStaff?: ITimeAndStaff;
    additonalServiceName?: { name: string; price: number }[];
  },
  payload: any,
  t: (key: string) => string
) => ({
  bookingId: booking?.referenceCode || 'Something went wrong!',
  serviceName: ctx.service?.serviceTitle || '-',
  paymentMethod: booking?.paymentMethod || '-',
  date: booking?.appointmentDate || '-',
  fromTime: booking?.appointmentTimeFrom || '-',
  toTime: booking?.appointmentTimeTo || '-',
  firstName: booking?.personalInfo?.firstName || '-',
  lastName: booking?.personalInfo?.lastName || '',
  address: booking?.personalInfo?.address?.street,
  city: booking?.personalInfo?.address?.city,
  state: booking?.personalInfo?.address?.state,
  postalCode: booking?.personalInfo?.address?.postalCode,
  phone:
    booking?.personalInfo?.address?.phone ||
    payload?.personalInfo?.phone ||
    '-',
  tax: booking?.tax || '-',
  subtotal: booking?.subtotal || '-',
  total: booking?.total || '-',
  discount: booking?.discount || '-',
  packageId: payload?.packageId || '-',
  staff:
    ctx.timeAndStaff?.staff?.fullName || t('booking.any_available_provider'),
  bookingStatus: booking?.bookingStatus || '-',
  bookingNotes:
    booking?.personalInfo?.bookingNotes ||
    payload?.personalInfo?.bookingNotes ||
    '-',
  additonalService: ctx.additonalServiceName?.length
    ? ctx.additonalServiceName
    : [],
});

export interface IAdditionalService {
  id: string;
  serviceItem: string;
  price: number;
  images: string;
}

interface ITimeAndStaff {
  date: string;
  timeSlotId: string;
  time: string;
  staffId: string;
  staff: StaffMember;
  providerTimeZone?: string;
}

type StaffMember = {
  staffId: string;
  fullName: string;
  providerStaffId: string;
  isProvider: boolean;
};

type CalculationGet = {
  discount: number;
  discountId: string;
  serviceId: string;
  subtotal: number;
  tax: number;
  total: number;
};

const BookingPage = () => {
  const { t } = useTranslation();
  const { userId, customerAccountStatus } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [packages, setPackage] = useState<PackageGet>();
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isBookingCreateLoading, setIsBookingCreateLoading] =
    useState<boolean>(false);
  // Bumped to remount <DateAndStaff> and re-fetch slot/staff availability after
  // a 409 SLOT_UNAVAILABLE hold rejection (see handleBooking).
  const [slotGridRefreshKey, setSlotGridRefreshKey] = useState<number>(0);
  const [calculation, setCalculation] = useState<CalculationGet>();
  const [timeAndStaff, setTimeAndStaff] = useState<ITimeAndStaff>();
  // Store the full tax quote from payment service to display jurisdiction/rate
  const [taxQuote, setTaxQuote] = useState<any>(null);

  // Idempotency key for the create. Minted ONCE per slot/staff selection and
  // reused for the lifetime of that submit intent, so both the axios auto-retry
  // and a user re-click of the same slot reuse the same key and the backend/mo
  // dedupes (finding #3). Reset when the selection changes or booking succeeds.
  const idempotencyKeyRef = useRef<string>('');

  // The confirmed booking payload, kept for the success-page state built after
  // payment confirmation (packageId + phone fallbacks).
  const bookingPayloadRef = useRef<any>(null);

  // Live form context for the success-page state — read via ref so the
  // polling effect's closure never goes stale across renders.
  const successContextRef = useRef<{
    service?: ServiceGet;
    timeAndStaff?: ITimeAndStaff;
    additonalServiceName?: { name: string; price: number }[];
  }>({});
  useEffect(() => {
    successContextRef.current = { service, timeAndStaff, additonalServiceName };
  });

  useEffect(() => {
    if (timeAndStaff?.timeSlotId && timeAndStaff?.staffId) {
      idempotencyKeyRef.current = generateIdempotencyKey();
    } else {
      idempotencyKeyRef.current = '';
    }
  }, [timeAndStaff?.date, timeAndStaff?.timeSlotId, timeAndStaff?.staffId]);

  const [additonalServiceIds, setAdditonalServiceIds] = useState<string[]>([]);
  const [additonalServiceName, setAdditonalServiceName] = useState<
    { name: string; price: number }[]
  >([]);
  const [packageId, setSelectPackage] = useState<string>('');
  const [promoCodeResponse] = useState({
    discountId: '',
    promoCode: '',
  });
  // Payment method the customer selected on the booking form. 'Cash on Hand'
  // is the default (matches the backend paymentMethod enum, see
  // PackageCheckout.tsx) — online is only offered when Stripe is configured.
  const [paymentType, setPaymentType] = useState<string>('Cash on Hand');
  const hasStripePaymentKey = Boolean((STRIPE_PUBLISHABLE_KEY || '').trim());
  // ON = hidden. Fail-open (see featureFlags.ts) so an Unleash outage hides
  // card payment rather than showing it — cash-on-hand stays as the fallback.
  const hideCardPayment = useFeatureFlag(
    FEATURE_FLAGS.hideSlotBookingCardPayment
  );
  const showOnlinePaymentOption = hasStripePaymentKey && !hideCardPayment;
  const [additonalService, setAdditonalService] = useState<
    IAdditionalService[]
  >([]);

  const { mutateAsync: totalCalculateMutate } = useBookingTotalCalculate();
  const { mutateAsync: calculateTaxMutate } = useCalculateTax();

  const applyTaxQuote = (baseCalculation: CalculationGet, taxResponse: any) => {
    const quote =
      taxResponse?.quote ??
      taxResponse?.result?.quote ??
      taxResponse?.data?.quote ??
      taxResponse?.data?.result?.quote ??
      null;

    const merged = mergePaymentTax(baseCalculation, quote);
    if (
      Number.isFinite(Number(merged?.tax)) &&
      Number.isFinite(Number(merged?.total))
    ) {
      return merged;
    }

    const totalTaxMinor = Number(
      taxResponse?.quote?.totalTaxMinor ??
        taxResponse?.result?.quote?.totalTaxMinor ??
        taxResponse?.data?.quote?.totalTaxMinor ??
        taxResponse?.totalTaxMinor ??
        taxResponse?.result?.totalTaxMinor ??
        taxResponse?.data?.totalTaxMinor ??
        0
    );

    if (Number.isFinite(totalTaxMinor) && totalTaxMinor !== 0) {
      const tax = totalTaxMinor / 100;
      const subtotal = Number(baseCalculation?.subtotal || 0);
      const discount = Number(baseCalculation?.discount || 0);
      return {
        ...baseCalculation,
        tax,
        total: subtotal - discount + tax,
      };
    }

    const fallbackTax = Number(
      taxResponse?.totalTax ??
        taxResponse?.result?.totalTax ??
        taxResponse?.data?.totalTax ??
        taxResponse?.tax ??
        taxResponse?.result?.tax ??
        taxResponse?.data?.tax ??
        0
    );

    if (Number.isFinite(fallbackTax) && fallbackTax !== 0) {
      const subtotal = Number(baseCalculation?.subtotal || 0);
      const discount = Number(baseCalculation?.discount || 0);
      return {
        ...baseCalculation,
        tax: fallbackTax,
        total: subtotal - discount + fallbackTax,
      };
    }

    return baseCalculation;
  };

  const [dateAndStaffState, setDateAndStaffState] = useState<any>(null);

  // ── Online payment flow state ──────────────────────────────────────────
  // idle: booking not created yet → paying: Stripe PaymentElement shown →
  // confirming: payment authorized, polling booking status → confirmed:
  // booking confirmed (navigating to success) → failed: confirmation timed
  // out or the booking came back cancelled. No retry button: the first
  // authorization may already have succeeded, so retrying could authorize a
  // second PaymentIntent against the same booking. Declined cards retry
  // inline on the same intent inside the PaymentElement.
  const [paymentPhase, setPaymentPhase] = useState<PaymentPhase>('idle');
  const [createdBooking, setCreatedBooking] = useState<any>(null);
  const [paymentFailureMessage, setPaymentFailureMessage] = useState('');
  // Slot hold expiry countdown (120 seconds from hold time)
  const [holdExpiryTime, setHoldExpiryTime] = useState<number | null>(null);
  const [holdSecondsRemaining, setHoldSecondsRemaining] = useState<number>(0);

  // If the flag flips on mid-session while Online Payment is selected, fall
  // back to cash so the flow never continues toward a hidden payment method.
  useEffect(() => {
    if (!showOnlinePaymentOption && paymentType === 'Online Payment') {
      setPaymentType('Cash on Hand');
      setPaymentPhase('idle');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOnlinePaymentOption]);

  const { serviceId } = location.state || {};

  const { data } = useFetchServiceDataById(serviceId);
  const service: ServiceGet = useMemo(() => data?.serviceInfo || [], [data]);
  const { data: userData } = useFetchUserById(userId ? userId : '');
  const isInitialUserDataLoaded = useRef(false);

  console.log('userData: ', userData);
  console.log('SERVICE: ', service);

  const form = useForm<PersonalGet>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: {
        street: '',
        city: '',
        state: '',
        postalCode: '',
      },
      bookingNotes: undefined,
    },
    shouldUnregister: true,
    mode: 'onChange',
    resolver: yupResolver(personalInfoSchema) as any,
    reValidateMode: 'onChange',
  });

  const {
    watch,
    setValue,
    trigger,
    formState: { errors, touchedFields },
  } = form;

  // ── Telemetry refs (Track C) ─────────────────────────────────────────
  const submittedRef = useRef(false);
  const mountedAtRef = useRef(Date.now());
  const lastFieldRef = useRef<string | null>(null);
  const prevTouchedRef = useRef<Set<string>>(new Set());
  const stepReportedRef = useRef(false);

  useEffect(() => {
    if (!userData) return;
    setValue('firstName', userData?.user?.firstName || '');
    setValue('lastName', userData?.user?.lastName || '');
    setValue('email', userData?.user?.email || '');
    setValue('phone', userData?.user?.mobile || '');

    // Only set address fields on initial load — after that,
    // AddressSection manages its own selection (auto-select on add,
    // manual card click). Resetting to address[0] on every userData
    // change would overwrite the user's selection after add/update/remove
    // mutations trigger a query refetch.
    if (!isInitialUserDataLoaded.current) {
      setValue(
        'address.street',
        userData?.user?.address?.[0]?.addressLine1 || ''
      );
      setValue('address.city', userData?.user?.address?.[0]?.city || '');
      setValue('address.state', userData?.user?.address?.[0]?.state || '');
      setValue(
        'address.postalCode',
        userData?.user?.address?.[0]?.postalCode || ''
      );
      isInitialUserDataLoaded.current = true;
    }
    setError('');
  }, [userData, setValue]);

  const email = watch('email');
  const firstName = watch('firstName');
  const lastName = watch('lastName');
  const address = watch('address');

  const services = useMemo(() => {
    if (!data?.serviceInfo) return [];
    return Array.isArray(data.serviceInfo)
      ? data.serviceInfo
      : [data.serviceInfo];
  }, [data]);

  useEffect(() => {
    if (!services?.length || !packageId) return;

    let matchedPackage: any = null;

    services.forEach((srv: ServiceGet) => {
      const pkg = srv?.packages?.find(
        (p: PackageGet) => p?.packageId === packageId
      );

      if (pkg) matchedPackage = pkg;
    });

    setPackage(matchedPackage);
  }, [packageId, services]);

  useEffect(() => {
    const matchedAdditionalService = services?.map((srv: ServiceGet) => {
      const matched = srv?.additionalServices?.filter((as: AdditionalGet) =>
        additonalServiceIds.includes(as?.id ?? '')
      );
      return matched;
    });
    setAdditonalService(matchedAdditionalService[0] || []);
  }, [additonalServiceIds, services]);

  // Total price of the additional services the customer has selected.
  // Computed locally so the order-summary row updates instantly on
  // selection changes — the backend total-calculation is debounced and
  // only refreshes subtotal/tax/discount/total.
  const additionalServicesTotal = useMemo(
    () =>
      additonalService.reduce(
        (sum: number, a: IAdditionalService) => sum + (Number(a?.price) || 0),
        0
      ),
    [additonalService]
  );

  // ── Telemetry: booking.step_viewed (single-step booking form) ─────────
  useEffect(() => {
    if (stepReportedRef.current) return;
    stepReportedRef.current = true;
    track('booking.step_viewed', {
      stepIndex: 1,
      stepName: 'personal-info',
    });
  }, []);

  // ── Telemetry: booking.form_interacted on field blur ──────────────────
  // Fires once per field per visit. Field names only — never values.
  useEffect(() => {
    const touched = Object.keys(touchedFields || {});
    for (const field of touched) {
      if (prevTouchedRef.current.has(field)) continue;
      prevTouchedRef.current.add(field);
      const telemetryField = TELEMETRY_FIELD_MAP[field];
      if (!telemetryField) continue;
      lastFieldRef.current = telemetryField;
      const rawError = (errors as any)?.[field]?.type as string | undefined;
      track('booking.form_interacted', {
        fieldName: telemetryField,
        filled: Boolean(watch(field as any)),
        validationError: mapValidationError(rawError),
      });
    }
  }, [touchedFields, errors, watch]);

  // ── Telemetry: booking.abandoned on unmount or tab close ──────────────
  const fireAbandoned = (exitType: 'nav' | 'close') => {
    if (submittedRef.current) return;
    // Ignore StrictMode's simulated unmount (sub-second dwell).
    const totalMs = Date.now() - mountedAtRef.current;
    if (totalMs < 1000) return;
    submittedRef.current = true; // prevent double-fire
    track('booking.abandoned', {
      lastStep: 1,
      lastField: lastFieldRef.current,
      stepDurationMs: totalMs,
      totalDurationMs: totalMs,
      exitType,
    });
  };

  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') {
        fireAbandoned('close');
      }
    };
    document.addEventListener('visibilitychange', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      fireAbandoned('nav');
    };
  }, []);

  // ── 3DS redirect resume ────────────────────────────────────────────────
  // Stripe redirects back to /service/booking after a 3DS challenge with
  // payment_intent_* query params. Restore the identifiers persisted before
  // confirmation and continue straight to booking-status polling — the
  // PaymentIntent is already authorized at this point (no re-confirmation).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasStripeRedirect =
      params.get('payment_intent') || params.get('redirect_status');
    if (!hasStripeRedirect) return;

    const saved = sessionStorage.getItem(PAYMENT_RESUME_KEY);
    if (!saved) return;

    try {
      const { bookingId, referenceCode } = JSON.parse(saved);
      sessionStorage.removeItem(PAYMENT_RESUME_KEY);
      window.history.replaceState({}, '', window.location.pathname);
      if (bookingId || referenceCode) {
        setCreatedBooking({ bookingId, referenceCode });
        setPaymentPhase('confirming');
      }
    } catch (err) {
      console.error('Failed to restore payment resume state:', err);
      sessionStorage.removeItem(PAYMENT_RESUME_KEY);
    }
  }, []);

  // ── Slot-hold expiry countdown (120 seconds) ────────────────────────────
  // When a hold is successfully acquired, set the expiry time and countdown.
  useEffect(() => {
    if (paymentPhase !== 'paying' || !holdExpiryTime) return;

    let cancelled = false;
    let timer: NodeJS.Timeout;

    const updateCountdown = () => {
      if (cancelled) return;
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil((holdExpiryTime - now) / 1000));
      setHoldSecondsRemaining(remaining);

      if (remaining > 0) {
        timer = setTimeout(updateCountdown, 1000);
      }
    };

    updateCountdown();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [paymentPhase, holdExpiryTime]);

  // ── Shared success handoff ──────────────────────────────────────────────
  // Both the online-payment poll (card) and the direct cash path end with a
  // confirmed booking and must land on the same success page with the same
  // telemetry. Refs are read at call time so nothing here goes stale.
  const goToBookingSuccess = useCallback(
    (booking: any, paymentMethod: string) => {
      track('booking.confirmed', {
        bookingId: booking?.referenceCode || '',
        amountBand: toBand(Number(booking?.total) || 0),
        paymentMethod,
      });
      setPaymentPhase('confirmed');
      addToast({
        title: t('booking.booking_successful'),
        description: t('booking.booking_created'),
        radius: 'md',
        color: 'success',
      });
      // Invalidate booking-list queries so the Bookings page and Dashboard
      // recent-bookings table refresh without a manual reload.
      queryClient.invalidateQueries({ queryKey: ['get-all-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['USER_BOOKINGS'] });
      navigate('/booking/success', {
        state: buildBookingSuccessState(
          booking,
          successContextRef.current,
          bookingPayloadRef.current,
          t
        ),
        replace: true,
      });
    },
    [navigate, queryClient, t]
  );

  // ── Booking-status polling after payment authorization ─────────────────
  // After stripe.confirmPayment() resolves with requires_capture (payment
  // authorized), the backend captures the PaymentIntent and confirms the
  // booking. Poll GET /booking/:id until the booking hits a confirmed status,
  // then hand off to the success page. A deadline keeps the customer informed
  // instead of spinning forever.
  useEffect(() => {
    if (paymentPhase !== 'confirming') return;

    const bookingId = createdBooking?.bookingId || createdBooking?._id || '';
    const referenceCode = createdBooking?.referenceCode || '';
    if (!bookingId && !referenceCode) return;

    let cancelled = false;
    let pollTimer: number | undefined;
    const startedAt = Date.now();

    const pollOnce = async () => {
      try {
        const booking = bookingId
          ? await getBookingById(bookingId)
          : await getBookingByReference(referenceCode);

        if (cancelled || !booking) return;

        // Capture no longer auto-confirms the booking — a paid card booking
        // rests at Pending (isPaid stamped at capture) until the provider
        // accepts and moves it to Confirmed. "Pending + isPaid" is therefore
        // a terminal success for the card flow, not a reason to keep polling.
        const isPaidPending =
          booking.bookingStatus === 'Pending' &&
          (booking as { isPaid?: boolean }).isPaid === true;
        if (
          CONFIRMED_STATUSES.has(booking.bookingStatus || '') ||
          isPaidPending
        ) {
          goToBookingSuccess(booking, 'card');
          return;
        }
        if (booking.bookingStatus === 'Cancelled') {
          // Distinct from the timeout message: the booking itself is gone
          // (e.g. hold expired), so "still confirming" wording would mislead.
          setPaymentFailureMessage(t('booking.payment_booking_cancelled'));
          setPaymentPhase('failed');
          return;
        }
      } catch (err) {
        console.warn('Booking status poll failed:', err);
      }

      if (cancelled) return;
      if (Date.now() - startedAt >= BOOKING_CONFIRM_TIMEOUT_MS) {
        setPaymentFailureMessage(t('booking.payment_confirmation_pending'));
        setPaymentPhase('failed');
        return;
      }
      pollTimer = window.setTimeout(pollOnce, BOOKING_CONFIRM_POLL_MS);
    };

    pollOnce();

    return () => {
      cancelled = true;
      if (pollTimer) window.clearTimeout(pollTimer);
    };
  }, [paymentPhase, createdBooking, goToBookingSuccess, t]);

  const onSubmit = async (data: any) => {
    console.log('Ok', data);
  };

  // HANDLE CALCULATION
  useEffect(() => {
    const calculateTotal = async () => {
      if (!serviceId || !service || Object.keys(service).length === 0) {
        return;
      }

      if (service.isPackage && !packageId) {
        return;
      }

      setIsLoading(true);
      try {
        const isPromoCode = !!(
          promoCodeResponse?.discountId && promoCodeResponse?.promoCode
        );

        // Determine the correct discount ID
        const discountId = isPromoCode
          ? promoCodeResponse?.discountId
          : packages?.isDiscount
            ? packages?.discount?.discountId
            : service.isDiscount
              ? service.discount?.discountId
              : '';

        const finalPayload = {
          serviceId,
          packageId: packageId || '',
          additionalServiceIds: additonalServiceIds || [],
          discountId: discountId || '',
          promoCode: isPromoCode ? promoCodeResponse?.promoCode : '',
        };
        console.log('Final Payload for Calculation: ', finalPayload);

        const res = await totalCalculateMutate(finalPayload);
        if (res?.result) {
          // Call payment service tax API if address data is available, then
          // merge the tax into the calculation so the displayed total — and
          // the amount the payment service derives from the booking — includes
          // tax. A tax failure must not silently change the total.
          console.log('Calculation Result: ', res.result);
          console.log('address Result: ', address?.postalCode, address?.state);

          if (address?.postalCode && address?.state) {
            try {
              const taxPayload = buildTaxCalculationPayload({
                serviceId,
                businessId: service?.businessId || '',
                subtotal: Number(res.result.subtotal || 0),
                address,
                service,
                supplyDate: timeAndStaff?.date,
              });

              const taxRes = await calculateTaxMutate(taxPayload);
              const merged = applyTaxQuote(res.result, taxRes);
              setCalculation(merged);

              // Store the full tax quote for rendering jurisdiction/rate in the summary
              if (taxRes?.quote) {
                setTaxQuote(taxRes.quote);
              }
            } catch (taxError) {
              console.error(
                'Error fetching tax from payment service:',
                taxError
              );
              setCalculation(res.result);
              setTaxQuote(null);
            }
          } else {
            setCalculation(res.result);
            setTaxQuote(null);
          }
        }
      } catch (error) {
        console.error('Error calculating:', error);
      } finally {
        setIsLoading(false);
      }
    };

    // Debounce logic
    const handler = setTimeout(() => {
      calculateTotal();
    }, 300);

    return () => clearTimeout(handler);

    // address + timeAndStaff feed the tax payload, so they must re-trigger the
    // debounced calculation (e.g. choosing an address changes the tax).
  }, [
    additonalServiceIds,
    address,
    calculateTaxMutate,
    packageId,
    promoCodeResponse,
    serviceId,
    service,
    packages,
    timeAndStaff,
    totalCalculateMutate,
  ]);

  const getDiscountLabel = () => {
    const discount = service?.isPackage
      ? packages?.discount
      : service?.discount;
    if (!discount) return '';
    return `${discount.valueType !== 'percentage' ? '$' : ''}${discount.amount}${
      discount.valueType === 'percentage' ? '%' : ''
    } Save`;
  };

  // const handleVerifyPromoCode = async () => {
  //   try {
  //     setIsPromoloading(true);
  //     setPromoCodeError('');
  //     if (!promoCode) return;

  //     const discountId =
  //       service?.isDiscount && service?.discount?.discountType === 'promo-code'
  //         ? service?.discount?.discountId
  //         : '';

  //     const payload = {
  //       discountId: discountId,
  //       promoCode: promoCode,
  //     };
  //     console.log('PAYLOAD:payload', payload);
  //     const res = await verifyPromoCode(payload);
  //     console.log('PAYLOAD: ', res);

  //     if (res) {
  //       setPromoCodeError('success');
  //       setPromoCodeResponse(payload);
  //     }
  //   } catch (error) {
  //     setPromoCodeError('error');
  //     console.error('Error verifying promo code: ', error);
  //   } finally {
  //     setIsPromoloading(false);
  //   }
  // };

  // const removePromoCode = () => {
  //   setPromoCodeResponse({
  //     discountId: '',
  //     promoCode: '',
  //   });
  //   setPromoCode('');
  //   setPromoCodeError('');
  // };

  console.log('additionalServiceIds: ', additonalServiceIds);

  const handleBooking = async () => {
    console.log('Booking...');
    setError('');
    setIsBookingCreateLoading(true);

    // A booking already exists and is being paid — ignore re-submits so the
    // cleared idempotency key can't produce a duplicate booking.
    if (paymentPhase !== 'idle') {
      setIsBookingCreateLoading(false);
      return;
    }

    if (customerAccountStatus !== 'active') {
      addToast({
        title: t('common.error'),
        description: t('booking.activate_account'),
        radius: 'md',
        color: 'danger',
      });
      setIsBookingCreateLoading(false);
      return;
    }

    try {
      const isPhoneValid = await trigger('phone'); // 👈 only phone

      if (!isPhoneValid) {
        addToast({
          title: t('common.validation_error'),
          description: t('booking.fill_correctly'),
          radius: 'md',
          color: 'danger',
        });
        setIsBookingCreateLoading(false);
        return; // stop booking
      }
      const personalDetails = watch();
      if (errors && Object.keys(errors).length > 0) {
        addToast({
          title: t('common.validation_error'),
          description: t('booking.fill_correctly'),
          radius: 'md',
          color: 'danger',
        });
        setIsBookingCreateLoading(false);
        return;
      }

      // paymentType comes from the selector state (cash by default). The
      // backend records it as the booking's paymentMethod.

      const data: any = {
        additonalServiceIds,
        timeAndStaff,
        personalDetails,
        service,
        calculation,
        packageId,
        paymentType,
      };
      const payload = formatBookingPayload(data);

      if (!payload) {
        console.error('Something went wrong while formatting booking payload');
        throw new Error(
          'Something went wrong while formatting booking payload'
        );
      }

      await validateBookingPayload(payload, calculation);

      // ── Hold-before-booking (booking-slot atomicity) ──────────────────
      // Idempotency key: minted ONCE per slot/staff selection (see
      // idempotencyKeyRef above) and reused for the lifetime of that submit
      // intent, so both the axios auto-retry and a user re-click of the same
      // slot reuse the same key and the backend dedupes via its unique index.
      const idempotencyKey = idempotencyKeyRef.current;

      // Tentatively reserve the slot (+ staff) before committing. If the
      // backend doesn't expose /booking/hold (501/404), fall back to the
      // legacy direct POST /booking below.
      let hold: HoldSlotResponse | null = null;
      try {
        hold = await holdBookingSlot({
          serviceId: payload.serviceId,
          appointmentDate: payload.appointmentDate,
          timeSlotId: payload.timeSlotId,
          from: payload.appointmentTimeFrom,
          to: payload.appointmentTimeTo,
          staffId: payload.staffId,
        });
      } catch (holdError: any) {
        const holdStatus = holdError?.response?.status;

        if (holdStatus === 501 || holdStatus === 404) {
          // Older backend without the hold endpoint — proceed with the legacy
          // direct booking path below.
          console.warn(
            `Booking hold endpoint not available (status ${holdStatus}) — falling back to direct booking.`
          );
        } else if (holdStatus === 409) {
          // Slot (or staff) was taken between grid render and submit. Refresh
          // the slot/staff grid and DO NOT POST the booking.
          setSlotGridRefreshKey((k) => k + 1);
          const holdReason = holdError?.response?.data?.reason;
          const holdMsg =
            holdError?.response?.data?.message ||
            (holdReason === 'capacity' || holdReason === 'staff_conflict'
              ? t('booking.no_available_times')
              : t('booking.booking_failed_desc'));
          addToast({
            title: t('booking.booking_failed'),
            description: holdMsg,
            radius: 'md',
            color: 'danger',
          });
          setError(holdMsg);
          console.warn(
            'Booking slot hold rejected (409) with reason:',
            holdReason
          );
          return;
        } else {
          throw holdError;
        }
      }

      // Attach holdToken + idempotencyKey so the backend can confirm the held
      // slot atomically and dedupe duplicate submits. The slot identity
      // (serviceId/appointmentDate/appointmentTimeFrom/appointmentTimeTo) is
      // already part of the payload for the confirm step.
      const bookingPayload = buildBookingPayload(payload, hold, idempotencyKey);

      // Keep the confirmed payload for the success-page state built after
      // payment confirmation (packageId + phone fallbacks).
      bookingPayloadRef.current = payload;

      let res: any;
      try {
        res = await apiClient.post(
          `${SERVICE_ENDPOINT}/booking`,
          bookingPayload
        );
      } catch (bookingError: any) {
        // Compensate: release the held slot (best-effort) so other customers
        // can claim it. Never mask the original booking failure.
        if (hold?.holdToken) {
          try {
            await releaseHeldSlot({
              serviceId: hold.serviceId,
              date: hold.date,
              from: hold.from,
              to: hold.to,
              holdToken: hold.holdToken,
            });
          } catch (releaseError) {
            console.error(
              'Best-effort release of held slot failed:',
              releaseError
            );
          }
        }
        throw bookingError;
      }

      if (res.data?.success && res.status === 201) {
        const b = res.data?.booking;
        if (!b) {
          console.error(
            'Booking API returned 200 but no booking data was found'
          );
          throw new Error(
            "Something went wrong! Your booking might not have been saved correctly. Please check your dashboard, and if it's missing, contact support."
          );
        }

        // Telemetry: booking.submitted (201). booking.confirmed fires later
        // once payment authorization confirms the booking (poll effect).
        submittedRef.current = true;
        const bookingId = b.referenceCode || '';
        const amountBand = toBand(Number(b?.total) || 0);
        const rawMethod = String(
          b?.paymentMethod || 'Online Payment'
        ).toLowerCase();
        const paymentMethod =
          rawMethod.includes('card') || rawMethod.includes('online')
            ? 'card'
            : rawMethod.includes('wallet')
              ? 'wallet'
              : 'cash';
        track('booking.submitted', { bookingId, amountBand, paymentMethod });

        // Booking succeeded — clear the key so the next booking (a new submit
        // intent) mints a fresh one. A failed attempt leaves it intact so a
        // retry reuses the same key.
        idempotencyKeyRef.current = '';

        // Hand off to payment: a cash booking is complete immediately; an
        // online booking shows the Stripe PaymentElement and only navigates to
        // success once the payment is authorized and the booking confirms (see
        // the poll effect).
        setCreatedBooking(b);
        if (paymentType === 'Cash on Hand') {
          goToBookingSuccess(b, 'cash');
        } else {
          // Set hold expiry countdown (120 seconds from now)
          setHoldExpiryTime(Date.now() + 120000);
          setPaymentPhase('paying');
        }
      } else {
        throw new Error(res.data?.message || t('booking.booking_failed_desc'));
      }
    } catch (error: any) {
      addToast({
        title: t('booking.booking_failed'),
        description: error.message || t('booking.booking_failed_desc'),
        radius: 'md',
        color: 'danger',
      });
      setError(error.message || t('booking.booking_failed_retry'));
      console.log('Error booking: ', error);
    } finally {
      setIsBookingCreateLoading(false);
    }
  };

  const navigateToReactivateAccount = () => {
    navigate('/customer/customer-profile');
  };

  const handlePaymentMethodChange = (method: string) => {
    // If payment is in progress, reset to idle to allow switching methods.
    // This unmounts the PaymentElement and lets the user proceed with a new payment method.
    if (paymentPhase !== 'idle') {
      setPaymentPhase('idle');
    }
    setPaymentType(method);
  };

  return (
    <>
      <Seo
        title={t('booking.seo_title')}
        description={t('booking.seo_description')}
        canonicalPath="/service/booking"
      />
      <div className="">
        <div>
          <p className="mt-2 text-xl font-semibold text-primary">
            {t('booking.title')}
          </p>
          <p className="mt-1 text-xs text-gray-600">{t('booking.subtitle')}</p>
        </div>
        <div>
          {customerAccountStatus === ACCOUNT_STATUS.DEACTIVATED && (
            <div className="flex items-center justify-center w-full mt-2">
              <CustomAlert
                title={t('booking.account_deactivated')}
                description={t('booking.account_deactivated_desc')}
                color="danger"
                variant="faded"
                endContent={
                  <CustomButton
                    onPress={() => navigateToReactivateAccount()}
                    color="danger"
                    size="sm"
                    variant="solid"
                  >
                    {t('booking.reactivate_account')}
                  </CustomButton>
                }
              />
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center w-full mt-2">
              <CustomAlert description={error} color="danger" variant="faded" />
            </div>
          )}
          <FormProvider {...form}>
            <Form onSubmit={onSubmit}>
              <div className="flex flex-col xl:flex-row gap-5 items-start mt-5">
                {/* ===== LEFT COLUMN: Main Form Content ===== */}
                <div className="flex-1 min-w-0 flex flex-col gap-2">
                  {service?.isPackage && (
                    <PackageSection
                      data={service}
                      onChange={setSelectPackage}
                    />
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-2">
                    {/* Col 1: Calendar */}
                    <div className="min-w-0">
                      <DateAndStaff
                        key={slotGridRefreshKey}
                        data={service}
                        onChange={setTimeAndStaff}
                        onStateChange={setDateAndStaffState}
                      />
                    </div>

                    {/* Col 2: Time Slots */}
                    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 h-full">
                      <p className="text-body-bold mb-2.5">
                        {t('booking.appointment_time')}
                      </p>
                      {!dateAndStaffState ? (
                        <p className="text-caption">
                          {t('booking.choose_date_time')}
                        </p>
                      ) : dateAndStaffState.isPending ? (
                        <div className="w-full flex justify-center items-center py-2">
                          <SmallLoadingSpinner size="sm" />
                        </div>
                      ) : dateAndStaffState.isCheckTimeSlotError ? (
                        <p className="text-xs text-red-400">
                          {dateAndStaffState.checkTimeSlotError}
                        </p>
                      ) : dateAndStaffState.timeSlots.length === 0 ? (
                        <p className="text-caption">
                          {dateAndStaffState.availabilityMessage ||
                            t('booking.no_available_times')}
                        </p>
                      ) : (
                        <div className="grid grid-cols-2 gap-2 max-h-[600px] overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
                          {dateAndStaffState.timeSlots.map(
                            (time: any, index: number) => (
                              <button
                                key={index}
                                type="button"
                                onClick={() =>
                                  dateAndStaffState.setSelectTimeSlot(
                                    time?.timeSlotId
                                  )
                                }
                                className={`flex justify-center items-center gap-1.5 border font-medium px-3.5 py-1.5 rounded-full transition whitespace-nowrap ${
                                  dateAndStaffState.selectTimeSlot ===
                                  time?.timeSlotId
                                    ? 'border-secondary bg-secondary text-white'
                                    : 'border-gray-200 bg-white hover-primary-gradient'
                                }`}
                              >
                                <p
                                  className={
                                    dateAndStaffState.selectTimeSlot ===
                                    time?.timeSlotId
                                      ? 'text-white text-xs'
                                      : 'text-body'
                                  }
                                >{`${time?.from} - ${time?.to}`}</p>
                              </button>
                            )
                          )}
                        </div>
                      )}
                    </div>

                    {/* Col 3: Staff */}
                    {!dateAndStaffState?.availableStaff ||
                    dateAndStaffState.availableStaff.length !== 1 ? (
                      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 h-full">
                        <p className="text-title text-[15px] font-semibold pb-2 mb-3 border-b border-gray-100">
                          {t('booking.select_staff')}
                        </p>
                        {!dateAndStaffState ? (
                          <p className="text-caption">
                            {t('booking.choose_date_time')}
                          </p>
                        ) : dateAndStaffState.isPending ? (
                          <div className="w-full flex justify-center items-center py-2">
                            <SmallLoadingSpinner size="sm" />
                          </div>
                        ) : dateAndStaffState.availableStaff?.length === 0 ? (
                          <p className="text-caption">
                            {dateAndStaffState.availabilityMessage ||
                              t('booking.no_available_staff')}
                          </p>
                        ) : (
                          <div className="grid grid-cols-2 gap-2.5 max-h-[600px] overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
                            {(dateAndStaffState.availableStaff?.length || 0) >
                              1 && (
                              <button
                                type="button"
                                onClick={() =>
                                  dateAndStaffState.setSelectedStaffId(
                                    ANY_STAFF_ID
                                  )
                                }
                                className={`flex text-xs font-medium flex-col items-center gap-1 border rounded-xl px-3 py-2 min-w-[95px] max-w-[130px] transition ${
                                  dateAndStaffState.selectedStaffId ===
                                  ANY_STAFF_ID
                                    ? 'border-secondary bg-secondary/10'
                                    : 'border-gray-200 bg-white hover-primary-gradient'
                                }`}
                              >
                                {ANY_STAFF_LABEL}
                              </button>
                            )}
                            {dateAndStaffState.availableStaff?.map(
                              (s: StaffMember, index: number) => {
                                const isSelected =
                                  dateAndStaffState.selectedStaffId ===
                                  s?.staffId;
                                return (
                                  <button
                                    key={index}
                                    type="button"
                                    onClick={() =>
                                      dateAndStaffState.setSelectedStaffId(
                                        s?.staffId || ''
                                      )
                                    }
                                    className={`flex text-xs font-medium flex-col items-center gap-1 border rounded-xl px-3 py-2 min-w-[95px] max-w-[130px] transition ${
                                      isSelected
                                        ? 'border-secondary bg-secondary/10'
                                        : 'border-gray-200 bg-white hover-primary-gradient'
                                    }`}
                                  >
                                    {s?.fullName}
                                  </button>
                                );
                              }
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 h-full">
                        <p className="text-title text-[15px] font-semibold pb-2 mb-3 border-b border-gray-100">
                          {t('booking.staff')}
                        </p>
                        <div className="flex items-center gap-2 p-2 rounded-xl bg-secondary/5 border border-secondary/20">
                          <div className="w-10 h-10 bg-secondary/10 rounded-full flex items-center justify-center text-xs font-bold text-secondary">
                            {getInitials(
                              dateAndStaffState.availableStaff[0]?.fullName
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-gray-600">
                              Selected
                            </p>
                            <p className="text-body font-medium">
                              {dateAndStaffState.availableStaff[0]?.fullName}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  {service?.additionalServices?.length > 0 && (
                    <AdditionalDetails
                      data={service}
                      onChange={setAdditonalServiceIds}
                      onChangeNameAndPrice={setAdditonalServiceName}
                    />
                  )}
                  <AddressSection data={userData} />
                </div>

                {/* ===== RIGHT COLUMN: Order Summary ===== */}
                {/* <div className="w-full xl:w-[380px] xl:sticky xl:top-4 order-last xl:order-none shrink-0"> */}
                <div className="w-full xl:max-w-[380px] xl:sticky xl:top-4 order-last xl:order-none shrink-0">
                  {/* Step Indicator */}
                  <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-3 mb-3 overflow-hidden">
                    <p className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                      {t('booking.booking_progress', 'Booking Progress')}
                    </p>
                    <div className="flex items-center gap-2 text-xs">
                      <div
                        className={`flex items-center justify-center w-6 h-6 rounded-full text-white font-semibold ${timeAndStaff ? 'bg-secondary' : 'bg-gray-300'}`}
                      >
                        ✓
                      </div>
                      <span className="text-gray-600">Date & Staff</span>
                      <div
                        className={`flex-1 h-0.5 ${service?.additionalServices?.length > 0 ? 'bg-gray-200' : 'hidden'}`}
                      ></div>
                      {service?.additionalServices?.length > 0 && (
                        <>
                          <div
                            className={`flex items-center justify-center w-6 h-6 rounded-full text-white font-semibold ${additonalServiceIds.length > 0 ? 'bg-secondary' : 'bg-gray-300'}`}
                          >
                            {additonalServiceIds.length > 0 ? '✓' : '○'}
                          </div>
                          <span className="text-gray-600 whitespace-nowrap">
                            Add-ons
                          </span>
                          <div className="flex-1 h-0.5 bg-gray-200"></div>
                        </>
                      )}
                      <div
                        className={`flex items-center justify-center w-6 h-6 rounded-full text-white font-semibold ${address?.street ? 'bg-secondary' : 'bg-gray-300'}`}
                      >
                        {address?.street ? '✓' : '○'}
                      </div>
                      <span className="text-gray-600">Address</span>
                      <div className="flex-1 h-0.5 bg-gray-200"></div>
                      <div
                        className={`flex items-center justify-center w-6 h-6 rounded-full text-white font-semibold ${paymentPhase === 'confirmed' ? 'bg-success' : 'bg-gray-300'}`}
                      >
                        {paymentPhase === 'confirmed' ? '✓' : '○'}
                      </div>
                      <span className="text-gray-600 whitespace-nowrap">
                        Payment
                      </span>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                    {/* Header */}
                    <div className="px-4 py-3.5 border-b border-gray-100">
                      <p className="text-title text-[15px] font-semibold">
                        {t('booking.order_summary')}
                      </p>
                    </div>

                    <div className="p-4 flex flex-col gap-2">
                      {/* Service */}
                      <div>
                        <p className="text-body mb-1.5">
                          {t('booking.service')}
                        </p>
                        <div className="flex items-center border border-gray-200 p-2 rounded-xl transition bg-white">
                          <div className="min:w-16 min:h-16 bg-gray-100 rounded-lg overflow-hidden aspect-square">
                            <img
                              src={
                                service?.gallery?.[0]?.serviceImages?.[0] ||
                                fallbackImage
                              }
                              alt={
                                service?.serviceTitle ||
                                t('booking.service_image_alt')
                              }
                              className="w-16 h-16 object-cover aspect-square text-xs"
                            />
                          </div>

                          <div className="ml-3 flex flex-col justify-between flex-1">
                            <div className="flex justify-between items-start w-full">
                              <p className="text-body-bold truncate line-clamp-1">
                                {service?.serviceTitle || '-'}
                              </p>
                              {service?.isPackage
                                ? checkDiscountIsValid(packages?.discount) && (
                                    <CustomChip
                                      label={getDiscountLabel()}
                                      color="success"
                                      size="sm"
                                      startContent={
                                        <TbRosetteDiscountCheckFilled className="text-sm" />
                                      }
                                      classNames={{
                                        base: 'text-xs',
                                        content: 'text-xs',
                                      }}
                                    />
                                  )
                                : checkDiscountIsValid(service?.discount) && (
                                    <CustomChip
                                      label={getDiscountLabel()}
                                      color="success"
                                      size="sm"
                                      startContent={
                                        <TbRosetteDiscountCheckFilled className="text-sm" />
                                      }
                                      classNames={{
                                        base: 'text-xs',
                                        content: 'text-xs',
                                      }}
                                    />
                                  )}
                            </div>
                            {service?.isPackage && (
                              <p className="text-body">
                                {t('booking.package')}:{' '}
                                {packages?.packageName || '-'}
                              </p>
                            )}

                            {service?.isPackage ? (
                              <p className="text-body-bold mt-1">
                                {formatCurrency(Number(packages?.price)) || '-'}
                              </p>
                            ) : (
                              <p className="text-body-bold mt-1">
                                {formatCurrency(Number(service?.price)) || '-'}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Appointment Details */}
                      <div className="min-w-full">
                        <p className="text-caption font-semibold uppercase tracking-wide mb-1.5">
                          {t('booking.appointment_details')}
                        </p>
                        <div className="grid grid-cols-2 w-full gap-2 items-start border border-gray-100 bg-gray-50 p-3 rounded-xl transition">
                          <div className="flex flex-col gap-1.5">
                            <p className="text-body text-gray-500">
                              {t('booking.appointment_date')}
                            </p>
                            <div className="flex flex-initial items-center gap-1.5">
                              <LuCalendarCheck className="text-secondary" />
                              <p className="text-body-bold">
                                {moment(timeAndStaff?.date).format(
                                  'DD MMM YYYY'
                                ) || '-'}
                              </p>
                            </div>
                          </div>

                          <div className="flex flex-col gap-1.5">
                            <p className="text-body text-gray-500">
                              {t('booking.appointment_time')}
                            </p>
                            <div className="flex flex-initial items-center gap-1.5">
                              <LuClock3 className="text-secondary" />
                              <p className="text-body-bold">
                                {timeAndStaff?.time || '-'}
                                {timeAndStaff?.providerTimeZone && (
                                  <span className="text-body text-gray-500 ml-1">
                                    ({timeAndStaff.providerTimeZone})
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>

                          <div className="flex flex-col gap-1.5 col-span-2">
                            <p className="text-body text-gray-500">
                              {t('booking.select_staff')}
                            </p>
                            <div className="flex flex-initial items-center gap-1.5">
                              <LuUser className="text-secondary" />
                              <p className="text-body-bold">
                                {timeAndStaff?.staffId === ANY_STAFF_ID
                                  ? ANY_STAFF_LABEL
                                  : timeAndStaff?.staff?.fullName || '-'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Additional Services */}
                      {additonalService?.length > 0 && (
                        <div className="min-w-full">
                          <p className="text-body mb-1.5">
                            {t('booking.additional_service')}
                          </p>
                          <div className="flex flex-col gap-1.5">
                            {additonalService?.map(
                              (a: IAdditionalService, index: number) => (
                                <div
                                  key={index}
                                  className="flex flex-row items-center border border-gray-200 p-1.5 rounded-xl transition bg-white"
                                >
                                  <span className="w-10 h-10 bg-gray-100 rounded-lg overflow-hidden">
                                    <img
                                      src={a?.images || fallbackImage}
                                      alt={t('booking.service_alt')}
                                      className="w-16 h-16 object-cover aspect-square"
                                    />
                                  </span>

                                  <div className="flex flex-col text-left ml-3">
                                    <p className="text-body">
                                      {a?.serviceItem || '-'}
                                    </p>
                                    <p className="text-body-bold">
                                      {formatCurrency(Number(a?.price)) || '-'}
                                    </p>
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}

                      {/* Customer Information */}
                      <div className="w-full">
                        <p className="text-caption font-semibold uppercase tracking-wide my-2">
                          {t('booking.contact_information')}
                        </p>

                        <div className="flex items-center gap-2.5 border border-gray-100 bg-gray-50 rounded-xl p-3">
                          <div className="w-8 h-8 rounded-full bg-secondary text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {getInitials(
                              [firstName, lastName].filter(Boolean).join(' ')
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-body-bold truncate">
                              {[firstName, lastName]
                                .filter(Boolean)
                                .join(' ') || '-'}
                            </p>
                            <p className="text-body text-gray-500 truncate">
                              {email || '-'}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-start gap-2 mt-2 px-1">
                          <SlLocationPin className="text-secondary text-sm mt-0.5 flex-shrink-0" />
                          {address?.street ? (
                            <p className="text-body break-words">
                              {address?.street}, {address?.city}{' '}
                              {address?.state} {address?.postalCode}
                            </p>
                          ) : (
                            <p className="text-body text-gray-400">
                              {t('booking.no_saved_addresses')}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Payment (Stripe PaymentElement) */}
                      <div className="min-w-full">
                        <p className="text-caption font-semibold uppercase tracking-wide my-2">
                          {t('booking.payment_method')}
                        </p>

                        {/* Payment method selector - always visible */}
                        <div className="flex flex-col gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              handlePaymentMethodChange('Cash on Hand')
                            }
                            className={`flex flex-row items-center border p-2 rounded-xl transition w-full text-left cursor-pointer ${
                              paymentType === 'Cash on Hand'
                                ? 'border-primary bg-primary/10'
                                : 'border-gray-200 bg-white'
                            }`}
                          >
                            <span className="w-9 h-9 rounded-md overflow-hidden flex-shrink-0">
                              <img
                                src={cashIcon}
                                className="w-9 h-9 object-cover"
                                alt={t('booking.cash_payment_alt')}
                              />
                            </span>
                            <div className="flex flex-col text-left ml-3">
                              <p className="text-body">
                                {t(
                                  'booking.pay_cash_after_service',
                                  'Pay in cash after service'
                                )}
                              </p>
                            </div>
                          </button>

                          {showOnlinePaymentOption ? (
                            <button
                              type="button"
                              onClick={() =>
                                handlePaymentMethodChange('Online Payment')
                              }
                              className={`flex flex-row items-center border p-2 rounded-xl transition w-full text-left cursor-pointer ${
                                paymentType === 'Online Payment'
                                  ? 'border-primary bg-primary/10'
                                  : 'border-gray-200 bg-white'
                              }`}
                            >
                              <span className="w-9 h-9 rounded-md overflow-hidden flex-shrink-0">
                                <img
                                  src={cardIcon}
                                  className="w-9 h-9 object-cover"
                                  alt={t('booking.online_payment_alt')}
                                />
                              </span>
                              <div className="flex flex-col text-left ml-3">
                                <p className="text-body">
                                  {t('booking.online_payment')}
                                </p>
                                <p className="text-[10px] text-gray-400">
                                  {t('booking.payment_after_create_note')}
                                </p>
                              </div>
                            </button>
                          ) : (
                            !hasStripePaymentKey && (
                              <div className="border border-dashed border-gray-300 bg-gray-50 text-gray-500 rounded-xl p-3 text-xs">
                                {t('booking.payment_not_configured')}
                              </div>
                            )
                          )}
                        </div>

                        {/* Payment status messages and forms */}
                        {paymentPhase === 'paying' &&
                          holdSecondsRemaining > 0 && (
                            <div className="flex items-center gap-2 border border-amber-200 bg-amber-50 p-3 rounded-xl mt-4 text-xs">
                              <div className="flex-1">
                                <p className="font-semibold text-amber-900">
                                  {t(
                                    'booking.slot_hold_expiring',
                                    'Slot hold expires in'
                                  )}{' '}
                                  <span className="text-lg font-bold text-amber-600">
                                    {holdSecondsRemaining}s
                                  </span>
                                </p>
                                <p className="text-amber-700 text-[11px] mt-0.5">
                                  {t(
                                    'booking.complete_payment_to_confirm',
                                    'Complete your payment to confirm the booking'
                                  )}
                                </p>
                              </div>
                            </div>
                          )}

                        {paymentPhase === 'paying' && (
                          <div className="border border-gray-200 bg-white p-3 rounded-xl mt-4">
                            <PaymentSection
                              bookingId={
                                createdBooking?.referenceCode ||
                                createdBooking?._id ||
                                ''
                              }
                              serviceId={service?.serviceId || ''}
                              providerId={service?.providerId || ''}
                              timeSlotId={timeAndStaff?.timeSlotId || ''}
                              onAuthorized={() => setPaymentPhase('confirming')}
                              amount={Number(calculation?.total) || 0}
                            />
                          </div>
                        )}

                        {paymentPhase === 'confirming' && (
                          <div className="flex flex-col items-center gap-2 border border-gray-200 bg-secondary/10 p-4 rounded-xl mt-4">
                            <SmallLoadingSpinner size="sm" />
                            <p className="text-body-bold text-center">
                              {t('booking.payment_authorized_confirming')}
                            </p>
                          </div>
                        )}

                        {paymentPhase === 'failed' && (
                          <div className="flex flex-col gap-2 mt-4">
                            <CustomAlert
                              description={
                                paymentFailureMessage ||
                                t('booking.payment_failed')
                              }
                              color="danger"
                              variant="faded"
                            />
                          </div>
                        )}

                        {paymentPhase === 'confirmed' && (
                          <div className="flex flex-col items-center gap-1 border border-success/30 bg-success/10 p-4 rounded-xl mt-4">
                            <LuCalendarCheck className="text-success text-xl" />
                            <p className="text-body-bold">
                              {t('booking.payment_confirmed')}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Payment Summary */}
                      <div className="min-w-full">
                        <p className="text-caption font-semibold uppercase tracking-wide my-2">
                          {t('booking.payment_summary')}
                        </p>
                        <div className="flex flex-col gap-2">
                          {/* Base service subtotal */}
                          <div className="flex justify-between gap-2">
                            <p className="text-body">{t('booking.subtotal')}</p>
                            {isLoading ? (
                              <p className="text-[10px] text-gray-400">
                                {t('common.calculating')}
                              </p>
                            ) : (
                              <p className="text-body-bold">
                                {formatCurrency(
                                  Math.max(
                                    0,
                                    Number(calculation?.subtotal || 0) -
                                      additionalServicesTotal
                                  )
                                )}
                              </p>
                            )}
                          </div>

                          {/* Each add-on service as its own line */}
                          {additonalService?.length > 0 &&
                            additonalService.map(
                              (addon: IAdditionalService) => (
                                <div
                                  key={addon.id}
                                  className="flex justify-between gap-2"
                                >
                                  <p className="text-body">
                                    {addon.serviceItem}
                                  </p>
                                  <p className="text-body-bold">
                                    {formatCurrency(Number(addon.price) || 0)}
                                  </p>
                                </div>
                              )
                            )}

                          {/* Tax line — always show, even when zero */}
                          {!isLoading && (
                            <div className="flex justify-between gap-2">
                              <p className="text-body">
                                {getTaxLineLabel(taxQuote) || 'Tax'}
                              </p>
                              <p className="text-body-bold">
                                {formatCurrency(Number(calculation?.tax) || 0)}
                              </p>
                            </div>
                          )}

                          {/* Discount line — only show if > 0 */}
                          {!isLoading && Number(calculation?.discount) > 0 && (
                            <div className="flex justify-between gap-2">
                              <p className="text-body">Discount</p>
                              <p className="text-body-bold text-success">
                                -{formatCurrency(Number(calculation?.discount))}
                              </p>
                            </div>
                          )}

                          <Divider className="my-1" />

                          {/* Total */}
                          <div className="flex justify-between items-center gap-2 bg-secondary/10 rounded-xl px-3 py-2.5">
                            <p className="text-title font-bold">
                              {t('booking.total')}
                            </p>
                            {isLoading ? (
                              <p className="text-[10px] text-gray-400">
                                {t('common.calculating')}
                              </p>
                            ) : (
                              <p className="text-title text-base font-bold text-primary">
                                {formatCurrency(
                                  Number(calculation?.total) || 0
                                )}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Create Booking Button */}
                      <div className="w-full">
                        {customerAccountStatus === 'active' &&
                          paymentPhase === 'idle' && (
                            <CustomButton
                              label={
                                paymentType === 'Cash on Hand'
                                  ? t(
                                      'booking.confirm_booking',
                                      'Confirm Booking'
                                    )
                                  : t(
                                      'booking.continue_to_payment',
                                      'Continue to Payment'
                                    )
                              }
                              className="w-full"
                              color="primary"
                              size="md"
                              type="button"
                              onPress={handleBooking}
                              isLoading={isBookingCreateLoading}
                            />
                          )}

                        <div className="my-4 mx-1">
                          <CustomAlert
                            hideIcon={true}
                            title={t('booking.disclaimer_title')}
                            description={t('booking.disclaimer_desc')}
                            color="primary"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          </FormProvider>
        </div>
      </div>
    </>
  );
};

export default BookingPage;
